import React, { useMemo, useReducer, useEffect, useState } from "react";
import "./NJADVRIForm.css";
import { useLanguage } from "../contexts/LanguageContext";
import FormHeader from "./FormHeader";
import WelcomeModal from "./WelcomeModal";

// BucketDisplay component for showing bucket names with tooltips
const BucketDisplay: React.FC<{ bucketKey: string; value: number }> = ({ bucketKey, value }) => {
  const { t } = useLanguage();
  const [showTooltip, setShowTooltip] = useState(false);

  // Color coding for bucket values based on severity thresholds
  const getBucketColorClass = (bucketKey: string, value: number) => {
    switch (bucketKey) {
      case 'emotionalAbuse':
        return value >= 30 ? 'risk-level-high' : value >= 13 ? 'risk-level-moderate' : 'risk-level-low';
      case 'financialAbuse':
        return value >= 25 ? 'risk-level-high' : value >= 15 ? 'risk-level-moderate' : 'risk-level-low';
      case 'physicalAbuse':
        return value >= 9 ? 'risk-level-high' : value >= 1 ? 'risk-level-moderate' : 'risk-level-low';
      case 'techAbuse':
        return value >= 17 ? 'risk-level-high' : value >= 9 ? 'risk-level-moderate' : 'risk-level-low';
      case 'stalking':
        return value >= 11 ? 'risk-level-high' : value >= 6 ? 'risk-level-moderate' : 'risk-level-low';
      case 'perceivedSafety':
        return value > 15 ? 'risk-level-high' : value >= 8 ? 'risk-level-moderate' : 'risk-level-low';
      case 'depression':
        return value > 21 ? 'risk-level-high' : value >= 15 ? 'risk-level-moderate' : 'risk-level-low';
      case 'anxiety':
        return value >= 8 ? 'risk-level-high' : value >= 1 ? 'risk-level-moderate' : 'risk-level-low';
      case 'ptsd':
        return value >= 3 ? 'risk-level-high' : value >= 1 ? 'risk-level-moderate' : 'risk-level-low';
      case 'substanceFrequency':
        return value >= 8 ? 'risk-level-high' : value >= 4 ? 'risk-level-moderate' : 'risk-level-low';
      case 'substanceUse':
        return value >= 8 ? 'risk-level-high' : value >= 4 ? 'risk-level-moderate' : 'risk-level-low';
      default:
        return '';
    }
  };

  const colorClass = getBucketColorClass(bucketKey, value);

  return (
    <div className="bucket-display">
      <div className="bucket-name">
        {t(`form.scoring.buckets.${bucketKey}.name`)}
        <div 
          className="bucket-info-icon"
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
          title="Click for more information"
        >
          ⓘ
        </div>
        {showTooltip && (
          <div className="bucket-tooltip">
            {t(`form.scoring.buckets.${bucketKey}.tooltip`)}
          </div>
        )}
      </div>
      <span className={`bucket-value ${colorClass}`}>{value}</span>
    </div>
  );
};

// RiskLevelDisplay component for showing risk levels with color coding
const RiskLevelDisplay: React.FC<{ 
  label: string; 
  value: number; 
  level?: string; 
  showLevel?: boolean;
  category?: string; // Add category to determine color thresholds
}> = ({ label, value, level, showLevel = false, category }) => {
  const { t } = useLanguage();
  
  const getRiskLevelClass = (riskLevel: string) => {
    const levelLower = riskLevel.toLowerCase();
    if (levelLower === 'low') return 'risk-level-low';
    if (levelLower === 'moderate') return 'risk-level-moderate';
    if (levelLower === 'high') return 'risk-level-high';
    return '';
  };

  // Color coding based on category and value thresholds
  const getColorClass = (category: string, value: number) => {
    switch (category) {
      case 'abuserAccessLevel':
        return value > 12 ? 'risk-level-high' : value >= 8 ? 'risk-level-moderate' : 'risk-level-low';
      case 'abuserRiskLevel':
        return value >= 4 ? 'risk-level-high' : 'risk-level-low';
      case 'perceivedSafetyLevel':
        return value > 15 ? 'risk-level-high' : value >= 8 ? 'risk-level-moderate' : 'risk-level-low';
      case 'emotionalImpactTotal':
        return value >= 2 ? 'risk-level-high' : value >= 1 ? 'risk-level-moderate' : 'risk-level-low';
      default:
        return level ? getRiskLevelClass(level) : '';
    }
  };

  const colorClass = category ? getColorClass(category, value) : (level ? getRiskLevelClass(level) : '');

  return (
    <li>
      <span className="risk-level-label">{label}</span>
      <span className={`bucket-value ${colorClass}`}>
        {value}
        {showLevel && level && (
          <span className={`risk-level-text ${getRiskLevelClass(level)}`}>
            {' '}({t(`form.scoring.levels.${level.toLowerCase()}`)})
          </span>
        )}
      </span>
    </li>
  );
};

/**
 * NJADVRI Multi‑Step Webform (no branding)
 * - Progress bar + stepper
 * - Autosave to localStorage
 * - Keyboard accessible, screen‑reader friendly
 * - Scoring scaffolding mirrors the guide in the source PDF
 * - Includes a representative subset of questions; add the rest by
 *   extending the SECTIONS constant with the same pattern.
 *
 * Usage: Drop this component into any React/Next.js app.
 * Tailwind optional but classNames are vanilla‑safe.
 */

// ---------- Types ----------

type Option = {
  label: string;
  value: number | string | boolean;
};

type QBase = {
  id: string; // should match the questionnaire numbering where possible
  prompt: string;
  helperText?: string;
  required?: boolean;
  // how to aggregate this question into section subtotals (if applicable)
  scoring?: {
    // add the raw numeric answer to these named buckets
    buckets?: string[]; // e.g., ["emotionalAbuse"]
    // for Yes/No, map true/false to numeric values
    yes?: number;
    no?: number;
  };
};

type Question =
  | (QBase & { kind: "radio"; options: Option[] })
  | (QBase & { kind: "likert"; // Never..Very Often or safety scale
               options: Option[] })
  | (QBase & { kind: "yesno"; options?: never; })
  | (QBase & { kind: "text"; multiline?: boolean; placeholder?: string; })
  | (QBase & { kind: "date" })
  | (QBase & { kind: "number"; min?: number; max?: number; step?: number })

export type Section = {
  key: string;
  title: string;
  description?: string;
  questions: Question[];
  // how to roll up section scores into higher‑level buckets
  rollup?: {
    // e.g., { into: "abuseExperiences:emotional", fromBuckets: ["emotionalAbuse"] }
    into: string;
    fromBuckets: string[];
  }[];
};

// ---------- Question Library (Representative Subset) ----------

const LIKERT_0_4: Option[] = [
  { label: "Never or N/A", value: 0 },
  { label: "Rarely", value: 1 },
  { label: "Occasionally", value: 2 },
  { label: "Often", value: 3 },
  { label: "Very Often", value: 4 },
];

const SAFETY_0_4: Option[] = [
  { label: "Always Safe or N/A", value: 0 },
  { label: "Often Safe", value: 1 },
  { label: "Sometimes Safe", value: 2 },
  { label: "Rarely Safe", value: 3 },
  { label: "Not Safe At All", value: 4 },
];

// YES/NO scoring helper — only specifies yes/no mapping; buckets provided per-question
const YESNO: Readonly<Pick<NonNullable<QBase["scoring"]>, "yes" | "no">> = { yes: 4, no: 0 } as const;

// Helper to build yes/no questions quickly
const yn = (id: string, prompt: string, buckets: string[] = []): Question => ({
  id,
  prompt,
  kind: "yesno",
  scoring: { ...YESNO, buckets },
});

// Section I — Demographics (subset)
const SECTION_I: Section = {
  key: "demographics",
  title: "Section I. Demographics",
  questions: [
    {
      id: "isReassessment",
      prompt: "Is this a: New risk assessment or Re‑assessment?",
      kind: "radio",
      options: [
        { label: "New risk assessment", value: "new" },
        { label: "Re‑assessment", value: "reassess" },
      ],
    },
    {
      id: "relationshipToAbuser",
      prompt: "What is your relationship to your abuser?",
      kind: "radio",
      options: [
        { label: "Current partner", value: "current" },
        { label: "Former partner", value: "former" },
        { label: "Family member", value: "family" },
        { label: "Other", value: "other" },
      ],
    },
    { id: "ageGroup", prompt: "Which age group do you belong to?", kind: "radio", options: [
      { label: "Less than 25", value: "lt25" },
      { label: "26 – 35", value: "26_35" },
      { label: "36 – 45", value: "36_45" },
      { label: "46 or older", value: "46p" },
    ]},
  ],
};

// Section II Part 1 — Emotional abuse (Q4‑16; subset shown)
const SECTION_II_P1: Section = {
  key: "abuse_p1_emotional",
  title: "Section II. Abuse Experiences — Part 1 (Emotional)",
  description:
    "For each behavior in the past 6 months, choose how often it occurred.",
  questions: [
    { id: "q4", prompt: "Called you a name and/or criticized you.", kind: "likert", options: LIKERT_0_4, scoring: { buckets: ["emotionalAbuse"] } },
    { id: "q5", prompt: "Gave you angry stares or looks.", kind: "likert", options: LIKERT_0_4, scoring: { buckets: ["emotionalAbuse"] } },
    { id: "q6", prompt: "Made a decision without discussing it with you.", kind: "likert", options: LIKERT_0_4, scoring: { buckets: ["emotionalAbuse"] } },
    { id: "q7", prompt: "Put down your family and friends.", kind: "likert", options: LIKERT_0_4, scoring: { buckets: ["emotionalAbuse"] } },
    { id: "q9", prompt: "Said things to scare you (e.g., ‘something bad’ would happen).", kind: "likert", options: LIKERT_0_4, scoring: { buckets: ["emotionalAbuse"] } },
    // 15–16 are yes/no conditional in the tool; included unconditionally for simplicity
    yn("q15", "Been violently and constantly jealous (e.g., ‘If I can’t have you, no one can’).", ["emotionalAbuse"]),
    yn("q16", "Controlled most or all of your daily activities (friends, family, car use).", ["emotionalAbuse"]),
  ],
  rollup: [
    { into: "abuseExperiences:emotional", fromBuckets: ["emotionalAbuse"] },
  ],
};

// Section II Part 2 — Financial abuse (subset)
const SECTION_II_P2: Section = {
  key: "abuse_p2_financial",
  title: "Section II. Abuse Experiences — Part 2 (Financial/Work)",
  questions: [
    { id: "q25", prompt: "Made you ask them for money.", kind: "likert", options: LIKERT_0_4, scoring: { buckets: ["financialAbuse"] } },
    { id: "q26", prompt: "Demanded to know how money was spent (e.g., demanded receipts).", kind: "likert", options: LIKERT_0_4, scoring: { buckets: ["financialAbuse"] } },
    { id: "q27", prompt: "Kept financial information from you.", kind: "likert", options: LIKERT_0_4, scoring: { buckets: ["financialAbuse"] } },
    { id: "q29", prompt: "Spent money you needed for rent or other bills.", kind: "likert", options: LIKERT_0_4, scoring: { buckets: ["financialAbuse"] } },
    yn("q32", "Has your abuser ever made you lose a job?", ["financialAbuse"]),
  ],
  rollup: [
    { into: "abuseExperiences:financial", fromBuckets: ["financialAbuse"] },
  ],
};

// Section II Part 3 — Physical abuse (subset)
const SECTION_II_P3: Section = {
  key: "abuse_p3_physical",
  title: "Section II. Abuse Experiences — Part 3 (Physical)",
  questions: [
    { id: "q33", prompt: "Threatened to hit or throw something at you.", kind: "likert", options: LIKERT_0_4, scoring: { buckets: ["physicalAbuse"] } },
    { id: "q35", prompt: "Threw, hit, kicked, or smashed something.", kind: "likert", options: LIKERT_0_4, scoring: { buckets: ["physicalAbuse"] } },
    { id: "q36", prompt: "Pushed, grabbed, or shoved you.", kind: "likert", options: LIKERT_0_4, scoring: { buckets: ["physicalAbuse"] } },
    yn("q37", "Hit or punched you.", ["physicalAbuse"]),
    yn("q39", "Choked or strangled you.", ["physicalAbuse"]),
    yn("q42", "Does your abuser have access to a gun?", ["physicalAbuse"]),
    yn("q45", "If physical violence occurred, has severity/frequency increased over the past year?", ["physicalAbuse"]),
  ],
  rollup: [
    { into: "abuseExperiences:physical", fromBuckets: ["physicalAbuse"] },
  ],
};

// Section II Part 4 — Technology‑facilitated abuse (subset)
const SECTION_II_P4: Section = {
  key: "abuse_p4_tech",
  title: "Section II. Abuse Experiences — Part 4 (Technology)",
  questions: [
    { id: "q46", prompt: "Used your social networking account without permission.", kind: "likert", options: LIKERT_0_4, scoring: { buckets: ["techAbuse"] } },
    { id: "q48", prompt: "Sent so many messages it made you feel unsafe.", kind: "likert", options: LIKERT_0_4, scoring: { buckets: ["techAbuse"] } },
    { id: "q50", prompt: "Threatened to harm you via phone, text, or social media.", kind: "likert", options: LIKERT_0_4, scoring: { buckets: ["techAbuse"] } },
    { id: "q53", prompt: "Posted embarrassing images of you online.", kind: "likert", options: LIKERT_0_4, scoring: { buckets: ["techAbuse"] } },
  ],
  rollup: [
    { into: "abuseExperiences:technology", fromBuckets: ["techAbuse"] },
  ],
};

// Section II Part 5 — Stalking (subset)
const SECTION_II_P5: Section = {
  key: "abuse_p5_stalking",
  title: "Section II. Abuse Experiences — Part 5 (Stalking)",
  questions: [
    { id: "q54", prompt: "Made unwanted phone calls or messages.", kind: "likert", options: LIKERT_0_4, scoring: { buckets: ["stalking"] } },
    { id: "q55", prompt: "Watched/followed you from a distance or used devices/GPS.", kind: "likert", options: LIKERT_0_4, scoring: { buckets: ["stalking"] } },
    { id: "q56", prompt: "Showed up at home/work/school when not wanted.", kind: "likert", options: LIKERT_0_4, scoring: { buckets: ["stalking"] } },
    { id: "q58", prompt: "Snuck into home/car and did things to scare you.", kind: "likert", options: LIKERT_0_4, scoring: { buckets: ["stalking"] } },
  ],
  rollup: [
    { into: "abuseExperiences:stalking", fromBuckets: ["stalking"] },
  ],
};

// Section II Part 6 — Sexual abuse (subset)
const SECTION_II_P6: Section = {
  key: "abuse_p6_sexual",
  title: "Section II. Abuse Experiences — Part 6 (Sexual)",
  questions: [
    yn("q59", "Pressured you to have sex when you didn’t want to or in ways you didn’t want.", ["sexualAbuse"]),
    yn("q60", "Made you have oral sex against your will.", ["sexualAbuse"]),
    yn("q61", "Physically forced you to have sex.", ["sexualAbuse"]),
    yn("q62", "Physically attacked the sexual parts of your body.", ["sexualAbuse"]),
  ],
  rollup: [
    { into: "abuseExperiences:sexual", fromBuckets: ["sexualAbuse"] },
  ],
};

// Section III — Abuser Access (subset)
const SECTION_III: Section = {
  key: "abuser_access",
  title: "Section III. Abuser Access",
  questions: [
    yn("q64", "Is your abuser currently in jail?", ["abuserAccess"]),
    yn("q66", "Are you currently in a relationship with this person?", ["abuserAccess"]),
    yn("q68", "Are you currently living with your abuser?", ["abuserAccess"]),
    yn("q69", "If no, does your abuser know where you live?", ["abuserAccess"]),
    yn("q70", "Have you ever had a restraining order against your abuser?", ["abuserAccess"]),
    yn("q71", "If yes, did your abuser ever violate the restraining order?", ["abuserAccess"]),
  ],
  rollup: [
    { into: "abuser:access", fromBuckets: ["abuserAccess"] },
  ],
};

// Section IV — Abuser Risk (subset)
const SECTION_IV: Section = {
  key: "abuser_risk",
  title: "Section IV. Abuser Risk",
  questions: [
    yn("q73", "Has your abuser ever threatened or tried to commit suicide?", ["abuserRisk"]),
    yn("q74", "Is your abuser an alcoholic or problem drinker?", ["abuserRisk"]),
    yn("q75", "Does your abuser use illegal drugs?", ["abuserRisk"]),
    yn("q76", "Is your abuser gang affiliated?", ["abuserRisk"]),
  ],
  rollup: [
    { into: "abuser:risk", fromBuckets: ["abuserRisk"] },
  ],
};

// Section V — Perceptions of Safety (subset)
const SECTION_V: Section = {
  key: "perceived_safety",
  title: "Section V. Perceptions of Safety",
  questions: [
    { id: "q77", prompt: "In your home", kind: "likert", options: SAFETY_0_4, scoring: { buckets: ["perceivedSafety"] } },
    { id: "q79", prompt: "At your job or school", kind: "likert", options: SAFETY_0_4, scoring: { buckets: ["perceivedSafety"] } },
    { id: "q81", prompt: "At social gatherings (friends, gym, worship)", kind: "likert", options: SAFETY_0_4, scoring: { buckets: ["perceivedSafety"] } },
    { id: "q83", prompt: "How physically safe do you feel from your abuser today?", kind: "likert", options: SAFETY_0_4, scoring: { buckets: ["perceivedSafety"] } },
  ],
  rollup: [
    { into: "perception:safety", fromBuckets: ["perceivedSafety"] },
  ],
};

// Section VI — Emotional Health (subset)
const SECTION_VI_P1: Section = {
  key: "emotional_health_p1",
  title: "Section VI. Emotional Health — Part 1 (Depression)",
  questions: [
    { id: "q87", prompt: "Felt depressed.", kind: "likert", options: [
      { label: "Not at all", value: 0 },
      { label: "Several days (1–6)", value: 1 },
      { label: "More than half the days (7+)", value: 2 },
      { label: "Nearly every day", value: 3 },
    ], scoring: { buckets: ["depression"] } },
    { id: "q89", prompt: "Felt hopeful about the future.", kind: "likert", options: [
      { label: "Not at all", value: 3 },
      { label: "Several days (1–6)", value: 2 },
      { label: "More than half the days (7+)", value: 1 },
      { label: "Nearly every day", value: 0 },
    ], scoring: { buckets: ["depression"] } },
    { id: "q92", prompt: "Felt happy.", kind: "likert", options: [
      { label: "Not at all", value: 3 },
      { label: "Several days (1–6)", value: 2 },
      { label: "More than half the days (7+)", value: 1 },
      { label: "Nearly every day", value: 0 },
    ], scoring: { buckets: ["depression"] } },
  ],
  rollup: [ { into: "emotional:depression", fromBuckets: ["depression"] } ],
};

const SECTION_VI_P2: Section = {
  key: "emotional_health_p2",
  title: "Section VI. Emotional Health — Part 2 (Anxiety)",
  questions: [
    { id: "q95", prompt: "Feeling nervous, anxious, or on edge.", kind: "likert", options: [
      { label: "Not at all", value: 0 },
      { label: "Several days (1–6)", value: 1 },
      { label: "More than half the days (7+)", value: 2 },
      { label: "Nearly every day", value: 3 },
    ], scoring: { buckets: ["anxiety"] } },
    { id: "q100", prompt: "Feeling afraid as if something awful might happen.", kind: "likert", options: [
      { label: "Not at all", value: 0 },
      { label: "Several days (1–6)", value: 1 },
      { label: "More than half the days (7+)", value: 2 },
      { label: "Nearly every day", value: 3 },
    ], scoring: { buckets: ["anxiety"] } },
  ],
  rollup: [ { into: "emotional:anxiety", fromBuckets: ["anxiety"] } ],
};

const SECTION_VI_P3: Section = {
  key: "emotional_health_p3",
  title: "Section VI. Emotional Health — Part 3 (PTSD Symptoms)",
  questions: [
    { id: "q101", prompt: "Recurrent thoughts or memories of the abuse.", kind: "likert", options: [
      { label: "Not at all", value: 0 },
      { label: "Several days (1–6)", value: 1 },
      { label: "More than half the days (7+)", value: 2 },
      { label: "Nearly every day", value: 3 },
    ], scoring: { buckets: ["ptsd"] } },
    { id: "q108", prompt: "Feeling on guard.", kind: "likert", options: [
      { label: "Not at all", value: 0 },
      { label: "Several days (1–6)", value: 1 },
      { label: "More than half the days (7+)", value: 2 },
      { label: "Nearly every day", value: 3 },
    ], scoring: { buckets: ["ptsd"] } },
  ],
  rollup: [ { into: "emotional:ptsd", fromBuckets: ["ptsd"] } ],
};

const SECTION_VI_P4: Section = {
  key: "emotional_health_p4",
  title: "Section VI. Emotional Health — Part 4 (Substance Use)",
  questions: [
    { id: "q109", prompt: "In the last two weeks, how often did you drink alcohol or use drugs?", kind: "likert", options: [
      { label: "Not at all", value: 0 },
      { label: "Several days (1–6)", value: 1 },
      { label: "More than half the days (7+)", value: 2 },
      { label: "Nearly every day", value: 3 },
    ], scoring: { buckets: ["substanceFrequency"] } },
    yn("q110", "Felt you ought to cut down on drinking or drug use?", ["substanceUse"]),
    yn("q111", "People annoyed you by criticizing your use?", ["substanceUse"]),
    yn("q112", "Felt bad or guilty about your use?", ["substanceUse"]),
    yn("q113", "Had a drink/used first thing in the morning?", ["substanceUse"]),
  ],
  rollup: [
    { into: "emotional:substance", fromBuckets: ["substanceUse", "substanceFrequency"] },
  ],
};

// Ordered steps
const SECTIONS: Section[] = [
  SECTION_I,
  SECTION_II_P1,
  SECTION_II_P2,
  SECTION_II_P3,
  SECTION_II_P4,
  SECTION_II_P5,
  SECTION_II_P6,
  SECTION_III,
  SECTION_IV,
  SECTION_V,
  SECTION_VI_P1,
  SECTION_VI_P2,
  SECTION_VI_P3,
  SECTION_VI_P4,
];

// ---------- State ----------

type Answer = number | string | boolean | null;

type FormState = {
  step: number; // index in SECTIONS
  answers: Record<string, Answer>;
  touched: Record<string, boolean>;
  // cache of computed scores per bucket
  bucketTotals: Record<string, number>;
};

const initialState: FormState = {
  step: 0,
  answers: {},
  touched: {},
  bucketTotals: {},
};

type Action =
  | { type: "SET_STEP"; step: number }
  | { type: "ANSWER"; id: string; value: Answer }
  | { type: "TOUCH"; id: string }
  | { type: "RECALC" }
  | { type: "LOAD"; payload: FormState };

function reducer(state: FormState, action: Action): FormState {
  switch (action.type) {
    case "SET_STEP":
      return { ...state, step: action.step };
    case "ANSWER": {
      const answers = { ...state.answers, [action.id]: action.value };
      return { ...state, answers };
    }
    case "TOUCH":
      return { ...state, touched: { ...state.touched, [action.id]: true } };
    case "RECALC":
      return { ...state, bucketTotals: computeBucketTotals(state.answers) };
    case "LOAD":
      return action.payload;
    default:
      return state;
  }
}

// ---------- Scoring ----------

function computeBucketTotals(answers: Record<string, Answer>): Record<string, number> {
  // Iterate over all sections/questions and funnel into buckets
  const totals: Record<string, number> = {};
  const add = (key: string, val: number) => (totals[key] = (totals[key] || 0) + val);

  const visit = (q: Question) => {
    const raw = answers[q.id];
    if (raw == null) return;
    if (!q.scoring) return;

    const buckets = q.scoring.buckets || [];

    if (q.kind === "likert" || q.kind === "radio" || q.kind === "number") {
      const num = typeof raw === "number" ? raw : Number(raw);
      if (!Number.isNaN(num)) buckets.forEach((b) => add(b, num));
      return;
    }

    if (q.kind === "yesno") {
      const yn = raw === true || raw === "yes";
      const val = yn ? (q.scoring.yes ?? 0) : (q.scoring.no ?? 0);
      buckets.forEach((b) => add(b, val));
      return;
    }
  };

  SECTIONS.forEach((s) => s.questions.forEach(visit));
  return totals;
}

// Higher‑level rollups (maps bucket sums into named aggregates)
function computeRollups(bucketTotals: Record<string, number>) {
  const rollupTotals: Record<string, number> = {};
  for (const section of SECTIONS) {
    section.rollup?.forEach(({ into, fromBuckets }) => {
      const sum = fromBuckets.reduce((acc, b) => acc + (bucketTotals[b] || 0), 0);
      rollupTotals[into] = (rollupTotals[into] || 0) + sum;
    });
  }
  return rollupTotals;
}

// Apply waiver thresholds based on the guide tables (subset logic)
function evaluateWaiverGuidance(rollups: Record<string, number>) {
  // Abuse Experiences subscales (example thresholds from the guide)
  const emotional = rollups["abuseExperiences:emotional"] || 0;
  const financial = rollups["abuseExperiences:financial"] || 0;
  const physical = rollups["abuseExperiences:physical"] || 0;
  const technology = rollups["abuseExperiences:technology"] || 0;
  const stalking = rollups["abuseExperiences:stalking"] || 0;
  const sexual = rollups["abuseExperiences:sexual"] || 0;

  const abuseRiskLevelScore =
    (emotional >= 30 ? 2 : emotional >= 13 ? 1 : 0) +
    (financial >= 25 ? 4 : financial >= 15 ? 2 : 0) +
    (physical >= 9 ? 8 : physical >= 1 ? 4 : 0) +
    (technology >= 17 ? 8 : technology >= 9 ? 4 : 0) +
    (stalking >= 11 ? 8 : stalking >= 6 ? 4 : 0) +
    (sexual >= 4 ? 8 : sexual >= 1 ? 4 : 0);

  // Abuser risk/access (simplified aggregation)
  const abuserAccess = rollups["abuser:access"] || 0;
  const abuserRisk = rollups["abuser:risk"] || 0;
  const perceivedSafety = rollups["perception:safety"] || 0;

  const abuserAccessLevel = abuserAccess > 12 ? 2 : abuserAccess >= 8 ? 1 : 0;
  const abuserRiskLevel = abuserRisk >= 4 ? 2 : 0;
  const perceivedSafetyLevel = perceivedSafety > 15 ? 2 : perceivedSafety >= 8 ? 1 : 0;

  // Emotional Health — partial, based on subset
  const depression = rollups["emotional:depression"] || 0;
  const anxiety = rollups["emotional:anxiety"] || 0;
  const ptsd = rollups["emotional:ptsd"] || 0; // would be count of "yes" items in full tool
  const substance = rollups["emotional:substance"] || 0;

  const depressionLevel = depression > 21 ? 2 : depression >= 15 ? 1 : 0;
  const anxietyLevel = anxiety >= 8 ? 2 : anxiety >= 1 ? 1 : 0;
  const ptsdLevel = ptsd >= 3 ? 2 : ptsd >= 1 ? 1 : 0;
  const substanceLevel = substance >= 8 ? 2 : 0;

  const emotionalImpactTotal = depressionLevel + anxietyLevel + ptsdLevel + substanceLevel;

  // Waiver guidelines (per guide):
  // Abuse Experiences Overall Risk Level Score → 0–2 low, 3–7 moderate, 8+ high
  const abuseOverallLevel = abuseRiskLevelScore >= 8 ? "High" : abuseRiskLevelScore >= 3 ? "Moderate" : "Low";
  // Emotional Health Overall Impact Score → 0 low, 1 moderate, 2+ high
  const emotionalOverallLevel = emotionalImpactTotal >= 2 ? "High" : emotionalImpactTotal >= 1 ? "Moderate" : "Low";

  // Recommendation summary (illustrative; tailor to program rules)
  const waiverRecs: string[] = [];
  if (abuseOverallLevel === "High") waiverRecs.push("Grant all waivers (note: if high financial abuse, waive work activities).");
  else if (abuseOverallLevel === "Moderate") waiverRecs.push("Use assessor discretion based on context.");
  else waiverRecs.push("No waivers recommended based on abuse risk alone.");

  if (emotionalOverallLevel === "High") waiverRecs.push("Grant all waivers except child support cooperation.");
  else if (emotionalOverallLevel === "Moderate") waiverRecs.push("Assessor discretion for emotional‑health impact.");
  else waiverRecs.push("No waivers recommended based on emotional‑health impact alone.");

  return {
    abuseRiskLevelScore,
    abuseOverallLevel,
    abuserAccessLevel,
    abuserRiskLevel,
    perceivedSafetyLevel,
    emotionalImpactTotal,
    emotionalOverallLevel,
    waiverRecs,
  };
}

// ---------- UI Helpers ----------

// Helper function to get option key for translation
function getOptionKey(label: string): string {
  const optionMap: Record<string, string> = {
    "Never or N/A": "likert.never",
    "Rarely": "likert.rarely", 
    "Occasionally": "likert.occasionally",
    "Often": "likert.often",
    "Very Often": "likert.veryOften",
    "Always Safe or N/A": "safety.alwaysSafe",
    "Often Safe": "safety.oftenSafe",
    "Sometimes Safe": "safety.sometimesSafe", 
    "Rarely Safe": "safety.rarelySafe",
    "Not Safe At All": "safety.notSafe",
    "Not at all": "depression.notAtAll",
    "Several days (1–6)": "depression.severalDays",
    "More than half the days (7+)": "depression.moreThanHalf",
    "Nearly every day": "depression.nearlyEveryDay",
    "New risk assessment": "assessmentType.new",
    "Re‑assessment": "assessmentType.reassess",
    "Current partner": "relationship.current",
    "Former partner": "relationship.former", 
    "Family member": "relationship.family",
    "Other": "relationship.other",
    "Less than 25": "ageGroup.lt25",
    "26 – 35": "ageGroup.26_35",
    "36 – 45": "ageGroup.36_45",
    "46 or older": "ageGroup.46p",
    "Yes": "yesNo.yes",
    "No": "yesNo.no"
  };
  return optionMap[label] || label;
}


function Field({ q, value, onChange }: { q: Question; value: Answer; onChange: (v: Answer) => void }) {
  const { t } = useLanguage();
  const name = q.id;

  if (q.kind === "likert" || q.kind === "radio") {
    const opts = (q as any).options as Option[];
    return (
      <fieldset className="form-field">
        <legend>{t(`questions.${q.id}`) || q.prompt}</legend>
        <div className="options-grid">
          {opts.map((opt, i) => (
            <label key={i}>
              <input
                type="radio"
                name={name}
                value={String(opt.value)}
                checked={String(value) === String(opt.value)}
                onChange={() => onChange(opt.value)}
              />
              <span>{t(`options.${getOptionKey(opt.label)}`) || opt.label}</span>
            </label>
          ))}
        </div>
        {q.helperText && <p className="text-xs mt-1 text-gray-500">{q.helperText}</p>}
      </fieldset>
    );
  }

  if (q.kind === "yesno") {
    return (
      <fieldset className="form-field">
        <legend>{t(`questions.${q.id}`) || q.prompt}</legend>
        <div className="yesno-container">
          <label>
            <input type="radio" name={name} value="true" checked={value === true} onChange={() => onChange(true)} />
            <span>{t('options.yesNo.yes')}</span>
          </label>
          <label>
            <input type="radio" name={name} value="false" checked={value === false} onChange={() => onChange(false)} />
            <span>{t('options.yesNo.no')}</span>
          </label>
        </div>
      </fieldset>
    );
  }

  if (q.kind === "text") {
    return (
      <div className="form-field">
        <label>
          <div className="font-medium mb-2">{t(`questions.${q.id}`) || q.prompt}</div>
          {q.multiline ? (
            <textarea className="text-input" rows={4} placeholder={q.placeholder} value={(value as string) ?? ""} onChange={(e) => onChange(e.target.value)} />
          ) : (
            <input className="text-input" placeholder={q.placeholder} value={(value as string) ?? ""} onChange={(e) => onChange(e.target.value)} />
          )}
        </label>
      </div>
    );
  }

  if (q.kind === "number") {
    const { min, max, step } = q as any;
    return (
      <div className="form-field">
        <label>
          <div className="font-medium mb-2">{t(`questions.${q.id}`) || q.prompt}</div>
          <input type="number" className="text-input" min={min} max={max} step={step}
                 value={(value as number) ?? 0} onChange={(e) => onChange(Number(e.target.value))} />
        </label>
      </div>
    );
  }

  if (q.kind === "date") {
    return (
      <div className="form-field">
        <label>
          <div className="font-medium mb-2">{t(`questions.${q.id}`) || q.prompt}</div>
          <input type="date" className="text-input" value={(value as string) ?? ""} onChange={(e) => onChange(e.target.value)} />
        </label>
      </div>
    );
  }

  return null;
}

// ---------- Main Component ----------

export default function NJADVRIForm() {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { t, loading } = useLanguage();
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const totalSteps = SECTIONS.length;
  const curr = SECTIONS[state.step];

  // Show welcome modal on first visit
  useEffect(() => {
    const hasSeenWelcome = localStorage.getItem("njadvri_welcome_seen");
    if (!hasSeenWelcome) {
      setShowWelcomeModal(true);
    }
  }, []);

  // Autosave
  useEffect(() => {
    const saved = localStorage.getItem("njadvri_form_v1");
    if (saved) {
      try {
        const parsed = JSON.parse(saved) as FormState;
        dispatch({ type: "LOAD", payload: parsed });
      } catch {}
    }
  }, []);

  useEffect(() => {
    const payload: FormState = { ...state };
    localStorage.setItem("njadvri_form_v1", JSON.stringify(payload));
  }, [state]);

  // Recompute scores when answers change
  useEffect(() => {
    dispatch({ type: "RECALC" });
  }, [state.answers.step]); // noop; kept to show intended recomputation hook

  const bucketTotals = useMemo(() => computeBucketTotals(state.answers), [state.answers]);
  const rollups = useMemo(() => computeRollups(bucketTotals), [bucketTotals]);
  const guidance = useMemo(() => evaluateWaiverGuidance(rollups), [rollups]);

  if (loading) {
    return (
      <div className="njadvri-form">
        <div className="form-container">
          <div className="form-header">
            <h1>Loading...</h1>
          </div>
        </div>
      </div>
    );
  }

  const goNext = (e: React.MouseEvent) => { e.preventDefault(); dispatch({ type: "SET_STEP", step: Math.min(state.step + 1, totalSteps - 1) }); };
  const goPrev = (e: React.MouseEvent) => { e.preventDefault(); dispatch({ type: "SET_STEP", step: Math.max(state.step - 1, 0) }); };

  const onAnswer = (id: string, value: Answer) => dispatch({ type: "ANSWER", id, value });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Final recalc
    dispatch({ type: "RECALC" });
    // Set form as completed
    dispatch({ type: "SET_STEP", step: -1 }); // Use -1 to indicate completion
  };

  const handleStartOver = () => {
    // Reset form to initial state
    dispatch({ type: "LOAD", payload: initialState });
    // Clear localStorage
    localStorage.removeItem("njadvri_form_v1");
  };

  const handleCloseModal = () => {
    setShowWelcomeModal(false);
    localStorage.setItem("njadvri_welcome_seen", "true");
  };

  const handleStartAssessment = () => {
    setShowWelcomeModal(false);
    localStorage.setItem("njadvri_welcome_seen", "true");
  };

  // Show completion view if form is completed (step === -1)
  if (state.step === -1) {
    return (
      <div className="njadvri-form">
        <WelcomeModal 
          isOpen={showWelcomeModal}
          onClose={handleCloseModal}
          onStartAssessment={handleStartAssessment}
        />
        <FormHeader step={totalSteps - 1} totalSteps={totalSteps} />
        <div className="form-container">
          <div className="completion-view">
            <div className="completion-header">
              <h2>{t('form.completion.title')}</h2>
              <p>{t('form.completion.message')}</p>
            </div>
            
            {/* Scoring Summary */}
            <section className="scoring-summary">
              <h3>{t('form.scoring.title')}</h3>
              <div className="scoring-grid">
                <div className="scoring-section">
                  <h4>{t('form.scoring.bucketTotals')}</h4>
                  <ul className="scoring-list">
                    {Object.entries(bucketTotals).map(([k, v]) => (
                      <li key={k}>
                        <BucketDisplay bucketKey={k} value={v} />
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="scoring-section">
                  <h4>{t('form.scoring.riskAssessment')}</h4>
                  <ul className="scoring-list">
                    <RiskLevelDisplay 
                      label={t('form.scoring.abuseRiskLevelScore')}
                      value={guidance.abuseRiskLevelScore}
                      level={guidance.abuseOverallLevel}
                      showLevel={true}
                    />
                    <RiskLevelDisplay 
                      label={t('form.scoring.abuserAccessLevel')}
                      value={guidance.abuserAccessLevel}
                      category="abuserAccessLevel"
                    />
                    <RiskLevelDisplay 
                      label={t('form.scoring.abuserRiskLevel')}
                      value={guidance.abuserRiskLevel}
                      category="abuserRiskLevel"
                    />
                    <RiskLevelDisplay 
                      label={t('form.scoring.perceivedSafetyLevel')}
                      value={guidance.perceivedSafetyLevel}
                      category="perceivedSafetyLevel"
                    />
                    <RiskLevelDisplay 
                      label={t('form.scoring.emotionalImpactTotal')}
                      value={guidance.emotionalImpactTotal}
                      level={guidance.emotionalOverallLevel}
                      showLevel={true}
                      category="emotionalImpactTotal"
                    />
                  </ul>
                </div>
              </div>
            </section>

            <div className="completion-actions">
              <button onClick={handleStartOver} className="nav-button start-over">
                {t('form.completion.startOver')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="njadvri-form">
      <WelcomeModal 
        isOpen={showWelcomeModal}
        onClose={handleCloseModal}
        onStartAssessment={handleStartAssessment}
      />
      <FormHeader step={state.step} totalSteps={totalSteps} />
      <div className="form-container">

        <form onSubmit={handleSubmit}>
          <div className="form-section">
            <h2>{t(`sections.${curr.key}.title`) || curr.title}</h2>
            {curr.description && <p>{t(`sections.${curr.key}.description`) || curr.description}</p>}

            {curr.questions.map((q) => (
              <Field key={q.id} q={q} value={state.answers[q.id] ?? null} onChange={(v) => onAnswer(q.id, v)} />
            ))}

            <div className="navigation-container">
              <button type="button" onClick={goPrev} disabled={state.step === 0} className="nav-button back">{t('form.navigation.back')}</button>
              {state.step < totalSteps - 1 ? (
                <button type="button" onClick={goNext} className="nav-button next">{t('form.navigation.next')}</button>
              ) : (
                <button type="submit" className="nav-button finish">{t('form.navigation.finish')}</button>
              )}
            </div>
          </div>
        </form>

        {/* Live Scoring Summary */}
        <section className="scoring-summary">
          <h3>{t('form.scoring.title')}</h3>
          <div className="scoring-grid">
            <div className="scoring-section">
              <h4>{t('form.scoring.bucketTotals')}</h4>
              <ul className="scoring-list">
                {Object.keys(bucketTotals).length === 0 && <li><span className="bucket-name">{t('form.scoring.noResponses')}</span></li>}
                {Object.entries(bucketTotals).map(([k, v]) => (
                  <li key={k}>
                    <BucketDisplay bucketKey={k} value={v} />
                  </li>
                ))}
              </ul>
            </div>
            <div className="scoring-section">
              <h4>{t('form.scoring.riskAssessment')}</h4>
              <ul className="scoring-list">
                <RiskLevelDisplay 
                  label={t('form.scoring.abuseRiskLevelScore')}
                  value={guidance.abuseRiskLevelScore}
                  level={guidance.abuseOverallLevel}
                  showLevel={true}
                />
                <RiskLevelDisplay 
                  label={t('form.scoring.abuserAccessLevel')}
                  value={guidance.abuserAccessLevel}
                  category="abuserAccessLevel"
                />
                <RiskLevelDisplay 
                  label={t('form.scoring.abuserRiskLevel')}
                  value={guidance.abuserRiskLevel}
                  category="abuserRiskLevel"
                />
                <RiskLevelDisplay 
                  label={t('form.scoring.perceivedSafetyLevel')}
                  value={guidance.perceivedSafetyLevel}
                  category="perceivedSafetyLevel"
                />
                <RiskLevelDisplay 
                  label={t('form.scoring.emotionalImpactTotal')}
                  value={guidance.emotionalImpactTotal}
                  level={guidance.emotionalOverallLevel}
                  showLevel={true}
                  category="emotionalImpactTotal"
                />
              </ul>
            </div>
          </div>
          
        </section>

      </div>
    </div>
  );
}
